/**
 * モーショングラフィックスのジョブの途中経過 (候補) と中断
 * (docs/features/brand-cut-motion-graphics-plan.md §10.10.1)。
 *
 * - ジョブ状態 API (`GET /api/jobs/<id>`) は処理中の行の `result_data.candidates` に、check が通った版 (候補) の
 *   コンタクトシートと採点を積む。URL は 1 時間の署名 URL。
 * - `POST /api/jobs/<id>/motion/stop {pick?}` で「この版にする」(pick = round) / 「ここまでで決める」(pick なし)。
 *   受け付けると `result_data.motion_stop = {requestedAt, pick}` が返るようになる。
 */
import { apiClient } from '../../services/apiClient';
import type { AsyncJob } from '../../services/asyncJobApi';

export interface MotionCandidate {
  round: number;
  /** 採点の平均 (採点前は 0 / 未定) */
  average: number;
  /** 観点ごとの採点 (採点前は空) */
  scores: Record<string, number>;
  summary: string;
  passed: boolean;
  syncPassed: boolean;
  contactSheetUrl: string;
  stillUrls: string[];
  at: string;
}

export interface MotionStop {
  requestedAt: string;
  pick?: number;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

/** result_data.candidates を検証して取り出す (形の崩れた要素は捨てる・round 昇順・同じ round は後勝ち)。 */
export function parseMotionCandidates(raw: unknown): MotionCandidate[] {
  if (!Array.isArray(raw)) return [];
  const byRound = new Map<number, MotionCandidate>();
  for (const item of raw) {
    if (!isRecord(item)) continue;
    const round = Number(item.round);
    if (!Number.isFinite(round)) continue;
    const scores: Record<string, number> = {};
    if (isRecord(item.scores)) {
      for (const [k, v] of Object.entries(item.scores)) {
        const n = Number(v);
        if (Number.isFinite(n)) scores[k] = n;
      }
    }
    const average = Number(item.average);
    byRound.set(round, {
      round,
      average: Number.isFinite(average) ? average : 0,
      scores,
      summary: typeof item.summary === 'string' ? item.summary : '',
      passed: item.passed === true,
      syncPassed: item.syncPassed === true,
      contactSheetUrl: typeof item.contactSheetUrl === 'string' ? item.contactSheetUrl : '',
      stillUrls: Array.isArray(item.stillUrls) ? item.stillUrls.filter((u): u is string => typeof u === 'string') : [],
      at: typeof item.at === 'string' ? item.at : '',
    });
  }
  return [...byRound.values()].sort((a, b) => a.round - b.round);
}

/** result_data.motion_stop を取り出す (無い・形が違えば null)。 */
export function parseMotionStop(raw: unknown): MotionStop | null {
  if (!isRecord(raw) || typeof raw.requestedAt !== 'string') return null;
  const pick = Number(raw.pick);
  return raw.pick != null && Number.isFinite(pick)
    ? { requestedAt: raw.requestedAt, pick }
    : { requestedAt: raw.requestedAt };
}

/** 候補に採点が付いているか (採点前は scores が空)。 */
export function isCandidateScored(c: MotionCandidate): boolean {
  return Object.keys(c.scores).length > 0;
}

/**
 * ポーリングの 1 回分 (onProgress) から、生成キューの行 (ActiveJob) に写す値を作る。
 * stage は処理中だけ・候補と中断は処理中 / 順番待ちのどちらでも返っていれば写す (完了時は task の戻り値で消える)。
 */
export function motionJobProgressPatch(job: Pick<AsyncJob, 'status' | 'stage' | 'result_data'>): {
  waiting: boolean;
  stage: string | undefined;
  candidates: MotionCandidate[] | undefined;
  motionStop: MotionStop | null | undefined;
} {
  const rd = isRecord(job.result_data) ? job.result_data : {};
  const candidates = parseMotionCandidates(rd.candidates);
  return {
    waiting: job.status === 'pending',
    stage: job.status === 'processing' ? job.stage?.text : undefined,
    candidates: candidates.length > 0 ? candidates : undefined,
    motionStop: parseMotionStop(rd.motion_stop) ?? undefined,
  };
}

/** 中断後の段階表示。pick があれば「版 n を描画」、無ければ「ここまでで決めます」。 */
export function motionStopStageText(stop: Pick<MotionStop, 'pick'>): string {
  return stop.pick != null ? `中断を受け付けました (版 ${stop.pick} を描画します)` : 'ここまでで決めます';
}

/** 「この版にする」(pick) / 「ここまでで決める」(pick なし)。404 / 403 / 409 は呼び出し側でトースト。 */
export async function stopMotionJob(jobId: string, pick?: number): Promise<void> {
  await apiClient.post<{ ok: boolean }>(`/jobs/${jobId}/motion/stop`, pick != null ? { pick } : {});
}

/** stop の失敗を利用者向けの文言にする。 */
export function motionStopErrorMessage(err: unknown): string {
  const resp = (err as { response?: { status?: number; data?: { error?: string } } })?.response;
  if (resp?.data?.error) return resp.data.error;
  if (resp?.status === 409) return '生成がすでに終わっているか、まだ候補ができていないため止められませんでした';
  if (resp?.status === 404) return 'ジョブが見つかりませんでした';
  if (resp?.status === 403) return 'このジョブを操作する権限がありません';
  return '中断を受け付けられませんでした';
}
